const normalizeBatchKeyText = (value = '') => String(value)
  .replace(/\s+/g, ' ')
  .trim();

const normalizeBatchStack = (value = '', maxLines = 2) => String(value)
  .replace(/\s+at\s+/g, '\nat ')
  .split(/\r?\n/)
  .map((line) => line.trim())
  .filter(Boolean)
  .slice(0, maxLines)
  .join('\n');

export const buildSystemLogBatchKey = (payload = {}) => JSON.stringify([
  payload.sessionId,
  payload.portal,
  payload.systemVersion,
  payload.buildId,
  payload.level,
  payload.severity,
  payload.eventKind,
  payload.category,
  payload.type,
  payload.code,
  payload.source,
  payload.module,
  payload.operation,
  payload.functionName,
  payload.listenerName,
  payload.collectionPath,
  payload.route,
  payload.url,
  payload.region,
  payload.requestId,
  payload.correlationId,
  payload.userRole,
  payload.lastAction,
  normalizeBatchKeyText(payload.context),
  normalizeBatchStack(payload.stack, 2),
  normalizeBatchStack(payload.reactStack, 3),
  normalizeBatchStack(payload.callerStack, 4),
  normalizeBatchKeyText(payload.message),
].map((value) => String(value || '')));

export const createSystemLogBatcher = ({send, waitMs = 250, maxBatchSize = 100} = {}) => {
  const pending = new Map();
  const initializedKeys = new Set();
  const keepEarlier = (current, candidate) => {
    const currentTime = Date.parse(current);
    const candidateTime = Date.parse(candidate);
    return Number.isNaN(currentTime) || Number.isNaN(candidateTime) || currentTime <= candidateTime ? current : candidate;
  };
  const keepLater = (current, candidate) => {
    const currentTime = Date.parse(current);
    const candidateTime = Date.parse(candidate);
    return Number.isNaN(currentTime) || Number.isNaN(candidateTime) || currentTime >= candidateTime ? current : candidate;
  };

  const flushKey = async (key) => {
    const batch = pending.get(key);
    if (!batch) return false;
    pending.delete(key);
    if (batch.timer) clearTimeout(batch.timer);
    const payload = {
      ...batch.firstPayload,
      occurrenceCount: batch.count,
      firstOccurrence: batch.firstOccurrence,
      lastOccurrence: batch.lastOccurrence,
    };
    let sent = false;
    try {
      sent = await send(payload);
    } catch (error) {
      sent = false;
    }
    batch.resolvers.forEach((resolve) => resolve(sent === true));
    return sent === true;
  };

  const sendFirstOccurrence = async (key, payload, timestamp) => {
    let sent = false;
    try {
      sent = await send({
        ...payload,
        occurrenceCount: 1,
        firstOccurrence: timestamp,
        lastOccurrence: timestamp,
      }) === true;
    } catch (error) {
      sent = false;
    }
    if (!sent) initializedKeys.delete(key);
    return sent;
  };

  const enqueue = (payload) => {
    const key = buildSystemLogBatchKey(payload);
    const timestamp = payload.timestamp || new Date().toISOString();
    if (!initializedKeys.has(key)) {
      initializedKeys.add(key);
      return sendFirstOccurrence(key, payload, timestamp);
    }
    return new Promise((resolve) => {
      const current = pending.get(key) || {
        firstPayload: payload,
        firstOccurrence: timestamp,
        lastOccurrence: timestamp,
        count: 0,
        resolvers: [],
        timer: null,
      };
      current.count += 1;
      current.firstOccurrence = keepEarlier(current.firstOccurrence, timestamp);
      current.lastOccurrence = keepLater(current.lastOccurrence, timestamp);
      current.resolvers.push(resolve);
      pending.set(key, current);
      if (current.count >= maxBatchSize) {
        void flushKey(key);
        return;
      }
      if (!current.timer) {
        current.timer = setTimeout(() => {
          void flushKey(key);
        }, waitMs);
      }
    });
  };

  const flushAll = async () => Promise.all(Array.from(pending.keys()).map((key) => flushKey(key)));

  return {
    enqueue,
    flushAll,
    pendingCount: () => pending.size,
    initializedCount: () => initializedKeys.size,
  };
};
